import _ from 'lodash';
import { CircularProgress, FormControlLabel, Radio } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { FormProvider, useForm } from 'react-hook-form';

import NgpTwoButtonDialog from '../../dialog-two-button/NgpTwoButtonDialog';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import { AUTO_COMPLETE_DEFAULT, USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import NgpTextField from '../../form/NgpTextField';
import NgpRadioGroup from '../../form/NgpRadioGroup';
import { INTERNAL_ID_FIELD } from '../../form/FormConstants';

import IPAddressUtilities from '../../../utilities/IPAddressUtilities';

const FIELD_SUBNET = 'value.subnet';
const FIELD_DESCRIPTION = 'description';
const FIELD_EXCLUDE = 'exclude';

const formId = 'frmIpRules';
const saveButtonText = i18n._(t`Save`);
const cancelText = i18n._(t`Cancel`);
const uniqueErrorText = i18n._(t`This subnet has already been added`);
const invalidSubnetText = i18n._(t`Please enter a valid IPv4 subnet.`);
const defaultFormValues = {
    value: {
        subnet: '',
    },
    description: '',
    exclude: false,
};

function cleanupRule(dataToSave) {
    const subnet = _.get(dataToSave, 'value.subnet', '');
    const ipAddr = IPAddressUtilities.parse(_.trim(subnet));
    if (!ipAddr) return null;

    if (!IPAddressUtilities.isSubnet(subnet)) return null;

    dataToSave.value = {
        intAddr: {
            start: ipAddr.startAddress().toArray(),
            end: ipAddr.endAddress().toArray(),
        },
        start: ipAddr.startAddress().correctForm(),
        end: ipAddr.endAddress().correctForm(),
        subnet: _.trim(subnet),
    };
    return dataToSave;
}

export default function IpScopeForm({
    selectedRecord,
    selectedList = [],
    setSelectedRecord,
    open,
    setOpen,
    onConfirm,
}) {
    // Override shouldUnregister: the MUI Dialog unmounts children when closed,
    // which would unregister the Controller-backed field and race with the
    // open-time trigger() call below.
    const methods = useForm({
        ...USE_FORM_DEFAULT_OPTIONS,
        defaultValues: defaultFormValues,
        mode: 'onChange',
        shouldUnregister: false,
    });
    const internalId = useRef(null);
    const { handleSubmit, formState, reset, trigger } = methods;
    const [saving, setSaving] = useState(false);
    // react-hook-form v6 does not expose `errors` through the formState
    // proxy, so a bump after trigger() forces a re-render so children read
    // the freshly-set errors.
    const [, bumpAfterTrigger] = useState(0);

    // Keep the latest list in a ref so the validate closure (registered once
    // with the field) always sees fresh data without being re-created.
    const selectedListRef = useRef(selectedList);
    selectedListRef.current = selectedList;

    const validateSubnet = useCallback((value) => {
        if (!IPAddressUtilities.isSubnet(value)) {
            return invalidSubnetText;
        }
        const trimmed = _.toLower(_.trim(value));
        const duplicate = _.find(
            selectedListRef.current,
            (item) =>
                _.toLower(_.get(item, FIELD_SUBNET, '')) === trimmed && item[INTERNAL_ID_FIELD] !== internalId.current
        );
        return duplicate ? uniqueErrorText : true;
    }, []);

    const subnetRules = useMemo(() => ({ validate: { subnet: validateSubnet } }), [validateSubnet]);

    useEffect(() => {
        if (!open) return;

        if (!_.isEmpty(selectedRecord)) {
            internalId.current = selectedRecord[INTERNAL_ID_FIELD];
            reset(selectedRecord);
        }

        // Surface the required-field validation as soon as the dialog opens.
        // The setTimeout pushes the bump out of any batch RHF is in the
        // middle of, so the re-render reliably picks up the new errors.
        const id = window.setTimeout(() => {
            trigger(FIELD_SUBNET).then(() => bumpAfterTrigger((c) => c + 1));
        }, 0);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleCleanup = () => {
        internalId.current = null;
        setSelectedRecord(undefined);
        reset(defaultFormValues);
    };

    const handleFormSubmit = async (dataToSave = {}) => {
        if (internalId.current) {
            _.set(dataToSave, INTERNAL_ID_FIELD, internalId.current);
        }

        // Uniqueness / subnet format are enforced by the on-change
        // `validate.subnet` rule, so the submit button is already disabled
        // when invalid.
        setSaving(true);
        try {
            await onConfirm(cleanupRule(dataToSave));
            handleCleanup();
        } finally {
            setSaving(false);
        }
    };

    const startIcon = saving && <CircularProgress color="inherit" variant="indeterminate" size={16} />;

    return (
        <NgpTwoButtonDialog
            title={<Trans>Priority Interface IP Scope Rule</Trans>}
            confirmButtonText={saveButtonText}
            cancelButtonText={cancelText}
            open={open}
            setOpen={setOpen}
            onCloseFunc={handleCleanup}
            confirmButtonDisabled={!formState.isValid || saving}
            confirmButtonProps={{ startIcon }}
            callerWillCloseOnConfirm
            onConfirm={handleSubmit(handleFormSubmit)}
        >
            <FormProvider {...methods}>
                <NgpForm
                    formId={formId}
                    initialized
                    loading={false}
                    showSave={false}
                    formPadding={false}
                    showRequiredFieldsTip={false}
                    submitFunction={handleFormSubmit}
                >
                    <FormSection>
                        <NgpTextField
                            fieldName={FIELD_SUBNET}
                            label={i18n._(t`IP Scope`)}
                            required
                            fullWidth
                            fieldRules={subnetRules}
                            inputProps={{
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />

                        <NgpTextField
                            fieldName={FIELD_DESCRIPTION}
                            label={i18n._(t`Description`)}
                            fullWidth
                            inputProps={{
                                maxLength: 255,
                                autoComplete: AUTO_COMPLETE_DEFAULT,
                            }}
                        />
                        <NgpRadioGroup fieldName={FIELD_EXCLUDE}>
                            <FormControlLabel
                                value="false"
                                label={t`Include interfaces in this subnet`}
                                control={<Radio />}
                            />
                            <FormControlLabel
                                value="true"
                                label={t`Exclude interfaces in this subnet`}
                                control={<Radio />}
                            />
                        </NgpRadioGroup>
                    </FormSection>
                </NgpForm>
            </FormProvider>
        </NgpTwoButtonDialog>
    );
}
